import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContactMoldKind,
  MoldConcentration,
} from '@ivario-arc/shared/src/enums.ts';
import { classNames } from '@utils/classNames';
import { ParameterDto } from '@src/types/types';
import { ParameterListItemLeft } from '@components/atoms/ParameterListItemLeft.tsx';
import {
  getContactMoldConcentrationFromValue,
  getContactMoldKindFromValue,
} from '@utils/parameters.ts';
import { ActiveLink } from '@components/atoms/ActiveLink.tsx';
import { ParameterDetailPopup } from '@components/atoms/ParameterDetailPopup.tsx';

function ContactMoldParameterValue({
  concentration,
  kind,
}: {
  concentration: MoldConcentration;
  kind: ContactMoldKind;
}) {
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });

  let parameterColor = 'text-green';
  if (concentration === MoldConcentration.MEDIUM_MOLD) {
    parameterColor = 'text-orange';
  } else if (concentration === MoldConcentration.HIGH_MOLD) {
    parameterColor = 'text-error';
  }

  const displayValue =
    concentration === MoldConcentration.NO_MOLD
      ? t('keinBefund')
      : t(`moldConcentration.${concentration}`, { context: kind });

  return (
    <div className="flex flex-row justify-between md:flex-col-reverse w-full md:w-96">
      {kind === ContactMoldKind.NONE &&
        concentration !== MoldConcentration.NO_MOLD && (
          <div className="text-grey1">{t(`moldExposure.name`)}</div>
        )}
      <div
        className={classNames(
          'font-bold text-end md:text-start',
          parameterColor,
        )}
      >
        {displayValue}
      </div>
    </div>
  );
}

export const ContactMoldParameter = ({
  parameter,
}: PropsWithChildren<{ parameter: ParameterDto }>) => {
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });
  const { name, category } = parameter;
  const [showDetail, setShowDetail] = useState(false);

  const concentration = getContactMoldConcentrationFromValue(parameter.value);
  const kind = getContactMoldKindFromValue(parameter.value);

  let statusBorderColor = '';
  if (concentration === MoldConcentration.MEDIUM_MOLD) {
    statusBorderColor = 'border-2 border-orange';
  } else if (concentration === MoldConcentration.HIGH_MOLD) {
    statusBorderColor = 'border-2 border-error';
  }

  return (
    <div
      className={classNames(
        'rounded-bonusLarge bg-grey3light print:break-inside-avoid ',
        statusBorderColor,
      )}
      data-testid="contact-mold-parameter"
    >
      <div
        className={classNames(
          'flex items-start md:items-center p-2.5 md:p-5 md:space-x-8 justify-between md:flex-row flex-col',
        )}
        data-testid="contact-mold-parameter-header"
      >
        <ParameterListItemLeft
          subtitle={category}
          title={name ?? 'Unknown'}
          parameter={parameter}
          wide
        />
        <ContactMoldParameterValue concentration={concentration} kind={kind} />
        <ActiveLink
          onTap={() => setShowDetail(true)}
          isActive={true}
          className="hidden md:block"
        >
          <span className="text-ivarioBlue">{t('moreInfoOnParam')}</span>
        </ActiveLink>
      </div>
      {showDetail && (
        <ParameterDetailPopup
          parameter={parameter}
          onClose={() => setShowDetail(false)}
        />
      )}
    </div>
  );
};
